import React, { useEffect, useMemo, useState, useRef } from 'react';
import { RepeatIcon, SearchIcon, UserIcon, CreditCardIcon, ClockIcon, LinkIcon, MailIcon, PhoneIcon, CheckIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
interface RecurringPaymentProps {
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  onReferenceChange: (reference: string) => void;
  onSubscriptionModeChange?: (mode: 'existing' | 'new') => void;
}
const customers = [{
  id: 'cust_1',
  name: 'John Doe',
  email: 'john@example.com',
  cards: [{
    id: 'card_1',
    last4: '4242',
    brand: 'Visa',
    expiry: '04/27'
  }, {
    id: 'card_2',
    last4: '5555',
    brand: 'Mastercard',
    expiry: '07/24',
    isExpired: true
  }, {
    id: 'card_3',
    last4: '9012',
    brand: 'Amex',
    expiry: '11/28'
  }]
}, {
  id: 'cust_2',
  name: 'Jane Smith',
  email: 'jane@example.com',
  cards: [{
    id: 'card_4',
    last4: '1234',
    brand: 'Visa',
    expiry: '09/26'
  }, {
    id: 'card_5',
    last4: '8877',
    brand: 'Mastercard',
    expiry: '03/28'
  }]
}];
const intervalOptions = [{
  value: 'weekly',
  label: 'Weekly',
  days: 7
}, {
  value: 'biweekly',
  label: 'Every 2 weeks',
  days: 14
}, {
  value: 'monthly',
  label: 'Monthly',
  days: 30
}, {
  value: 'quarterly',
  label: 'Quarterly',
  days: 90
}, {
  value: 'biannually',
  label: 'Every 6 months',
  days: 180
}, {
  value: 'annually',
  label: 'Annually',
  days: 365
}];
const cardBrandLogo = (brand: string) => {
  const b = brand.toLowerCase();
  if (b === 'visa') return <img src="https://cdn.magicpatterns.com/uploads/uKgwD9hRGMCjPWbQQX2NK4/visa_logo.png" alt="Visa" className="h-4 object-contain" data-id="element-3437" />;
  if (b === 'mastercard') return <img src="https://cdn.magicpatterns.com/uploads/bYY7QK7c8BXLYhVKDVL5nS/mastercard_logo.png" alt="Mastercard" className="h-6 object-contain" data-id="element-3438" />;
  if (b === 'amex') return <img src="https://cdn.magicpatterns.com/uploads/kULTyrF2yfdM83fEevfsn1/amex_logo.svg" alt="Amex" className="h-6 object-contain" data-id="element-3439" />;
  return <span className="text-[10px] font-bold uppercase text-gray-500" data-id="element-3440">
      {brand}
    </span>;
};
const formatDateShort = (d: Date) => d.toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});
const formatDateFull = (d: Date) => `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
function addInterval(date: Date, interval: string): Date {
  const d = new Date(date);
  switch (interval) {
    case 'weekly':
      d.setDate(d.getDate() + 7);
      break;
    case 'biweekly':
      d.setDate(d.getDate() + 14);
      break;
    case 'monthly':
      d.setMonth(d.getMonth() + 1);
      break;
    case 'quarterly':
      d.setMonth(d.getMonth() + 3);
      break;
    case 'biannually':
      d.setMonth(d.getMonth() + 6);
      break;
    case 'annually':
      d.setFullYear(d.getFullYear() + 1);
      break;
  }
  return d;
}
export function RecurringPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSubscriptionModeChange
}: RecurringPaymentProps) {
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [trialDays, setTrialDays] = useState('0');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<(typeof customers)[0] | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [subscriptionMode, setSubscriptionMode] = useState<'existing' | 'new'>('existing');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Interval dropdown
  const [intervalSearch, setIntervalSearch] = useState('');
  const [intervalOpen, setIntervalOpen] = useState(false);
  const intervalRef = useRef<HTMLDivElement>(null);
  // Date picker
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  useEffect(() => {
    onSubscriptionModeChange?.(subscriptionMode);
  }, [subscriptionMode, onSubscriptionModeChange]);
  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (intervalRef.current && !intervalRef.current.contains(e.target as Node)) setIntervalOpen(false);
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) setShowDatePicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^\d.]/g, '');
    setAmount(v);
    onAmountChange(v);
  };
  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReference(e.target.value);
    onReferenceChange(e.target.value);
  };
  const handleSelectCustomer = (customer: (typeof customers)[0]) => {
    setSelectedCustomer(customer);
    setSelectedCard(null);
    setSearchTerm('');
    const validCards = customer.cards.filter(c => !c.isExpired);
    if (validCards.length > 0) setSelectedCard(validCards[0].id);
  };
  const filteredCustomers = searchTerm ? customers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  const filteredIntervals = intervalSearch ? intervalOptions.filter(o => o.label.toLowerCase().includes(intervalSearch.toLowerCase())) : intervalOptions;
  const selectedIntervalLabel = intervalOptions.find(o => o.value === billingInterval)?.label || '';
  // Calendar generation
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const calendarWeeks = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const last = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    const startDay = first.getDay();
    const daysInMonth = last.getDate();
    const weeks: (Date | null)[][] = [];
    let days: (Date | null)[] = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), i));
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) days.push(null);
        weeks.push(days);
        days = [];
      }
    }
    return weeks;
  }, [calendarMonth]);
  const isDatePast = (d: Date) => d < today;
  const isDateSelected = (d: Date) => startDate && d.getDate() === startDate.getDate() && d.getMonth() === startDate.getMonth() && d.getFullYear() === startDate.getFullYear();
  const isDateToday = (d: Date) => d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  // Generate billing cycle preview
  const billingPreview = useMemo(() => {
    const cycles: Array<{
      cycle: number;
      date: Date;
      amount: string;
      isTrial: boolean;
    }> = [];
    const effectiveStart = startDate || new Date();
    const trial = parseInt(trialDays) || 0;
    const trialEnd = new Date(effectiveStart);
    trialEnd.setDate(trialEnd.getDate() + trial);
    const maxCycles = billingCycles ? parseInt(billingCycles) : 12;
    const displayCycles = Math.min(maxCycles, 24);
    let currentDate = trial > 0 ? new Date(trialEnd) : new Date(effectiveStart);
    // If there's a trial, add trial entry
    if (trial > 0) {
      cycles.push({
        cycle: 0,
        date: new Date(effectiveStart),
        amount: '0',
        isTrial: true
      });
    }
    for (let i = 0; i < displayCycles; i++) {
      cycles.push({
        cycle: i + 1,
        date: new Date(currentDate),
        amount: amount || '0',
        isTrial: false
      });
      currentDate = addInterval(currentDate, billingInterval);
    }
    return cycles;
  }, [startDate, trialDays, billingCycles, billingInterval, amount]);
  return <div className="space-y-5" data-id="element-3441">
      {/* ─── SUBSCRIPTION TYPE ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-3442">
        <div className="p-6" data-id="element-3443">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-3444">
            Subscription type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3445">
            <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-3 ${subscriptionMode === 'existing' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSubscriptionMode('existing')} data-id="element-3446">
              <input type="radio" name="subMode" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={subscriptionMode === 'existing'} onChange={() => setSubscriptionMode('existing')} data-id="element-3447" />
              <div data-id="element-3448">
                <div className="flex items-center gap-2" data-id="element-3449">
                  <CreditCardIcon size={15} className="text-[#F7941D]" data-id="element-3450" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-3451">
                    Existing customer
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-3452">
                  Charge a saved payment method on a recurring schedule
                </p>
              </div>
            </div>
            <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-3 ${subscriptionMode === 'new' ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSubscriptionMode('new')} data-id="element-3453">
              <input type="radio" name="subMode" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={subscriptionMode === 'new'} onChange={() => setSubscriptionMode('new')} data-id="element-3454" />
              <div data-id="element-3455">
                <div className="flex items-center gap-2" data-id="element-3456">
                  <LinkIcon size={15} className="text-[#F7941D]" data-id="element-3457" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-3458">
                    New subscription link
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-3459">
                  Send a link to a customer to start a new subscription
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── CUSTOMER (EXISTING) ─── */}
      {subscriptionMode === 'existing' && <div className="bg-white rounded-xl border border-gray-200" data-id="element-3460">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-3461">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-3462">
              <UserIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-3463" />
              Customer
            </h3>
          </div>
          {!selectedCustomer ? <div className="p-6" data-id="element-3464">
              <div className="relative" data-id="element-3465">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3466">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-3467" />
                </div>
                <input type="text" className={`${inputClass} pl-10`} placeholder="Search by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3468" />
                {searchTerm && filteredCustomers.length > 0 && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-3469">
                    {filteredCustomers.map((customer, idx) => <button key={customer.id} type="button" onClick={() => handleSelectCustomer(customer)} className={`w-full px-4 py-3 text-left flex items-center hover:bg-gray-50 transition-colors ${idx < filteredCustomers.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-3470">
                        <div className="h-8 w-8 rounded-full bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-3471">
                          <UserIcon size={14} className="text-[#F7941D]" data-id="element-3472" />
                        </div>
                        <div data-id="element-3473">
                          <p className="text-sm font-medium text-gray-900" data-id="element-3474">
                            {customer.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-3475">
                            {customer.email}
                          </p>
                        </div>
                      </button>)}
                  </div>}
              </div>
            </div> : <>
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200" data-id="element-3476">
                <div className="flex items-center min-w-0" data-id="element-3477">
                  <div className="h-9 w-9 rounded-full bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-3478">
                    <UserIcon size={16} className="text-[#F7941D]" data-id="element-3479" />
                  </div>
                  <div className="min-w-0" data-id="element-3480">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-3481">
                      {selectedCustomer.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-3482">
                      {selectedCustomer.email}
                    </p>
                  </div>
                </div>
                <button onClick={() => {
            setSelectedCustomer(null);
            setSelectedCard(null);
          }} className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors flex-shrink-0 ml-4" data-id="element-3483">
                  Change
                </button>
              </div>
              <div data-id="element-3484">
                {selectedCustomer.cards.map((card, idx) => {
            const expired = !!(card as any).isExpired;
            return <div key={card.id} className={`flex items-center px-6 py-4 transition-colors ${expired ? 'opacity-50 cursor-not-allowed bg-gray-50/50' : selectedCard === card.id ? 'bg-[#F7941D]/5 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} ${idx < selectedCustomer.cards.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => {
              if (!expired) setSelectedCard(card.id);
            }} data-id="element-3485">
                      <input type="radio" name="recurringCard" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D]/20 border-gray-300 flex-shrink-0" checked={selectedCard === card.id} disabled={expired} onChange={() => {
                if (!expired) setSelectedCard(card.id);
              }} data-id="element-3486" />
                      <div className="ml-4 flex-1 flex items-center justify-between" data-id="element-3487">
                        <div className="flex items-center" data-id="element-3488">
                          <div className={`h-8 w-12 border rounded flex items-center justify-center mr-3 overflow-hidden ${expired ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`} data-id="element-3489">
                            {cardBrandLogo(card.brand)}
                          </div>
                          <div data-id="element-3490">
                            <p className={`text-sm font-medium ${expired ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-3491">
                              {card.brand} •••• {card.last4}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5" data-id="element-3492">
                              <p className={`text-xs ${expired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3493">
                                Expires {card.expiry}
                              </p>
                              {expired && <span className="text-[10px] font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-3494">
                                  Expired
                                </span>}
                            </div>
                          </div>
                        </div>
                        {!expired && selectedCard === card.id && <CheckIcon size={16} className="text-[#F7941D] flex-shrink-0" data-id="element-3495" />}
                      </div>
                    </div>;
          })}
              </div>
            </>}
        </div>}

      {/* ─── CUSTOMER INFO (NEW) ─── */}
      {subscriptionMode === 'new' && <div className="bg-white rounded-xl border border-gray-200" data-id="element-3496">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-3497">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-3498">
              <UserIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-3499" />
              Customer information
            </h3>
            <p className="text-xs text-gray-500 mt-1" data-id="element-3500">
              We'll send a subscription link to this customer.
            </p>
          </div>
          <div className="p-6 space-y-4" data-id="element-3501">
            <div data-id="element-3502">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3503">
                Name{' '}
                <span className="normal-case text-gray-400" data-id="element-3504">(optional)</span>
              </label>
              <div className="relative" data-id="element-3505">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3506">
                  <UserIcon size={14} className="text-gray-400" data-id="element-3507" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder="John Doe" value={customerName} onChange={e => setCustomerName(e.target.value)} data-id="element-3508" />
              </div>
            </div>
            <div data-id="element-3509">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3510">
                Email <span className="text-red-400" data-id="element-3511">*</span>
              </label>
              <div className="relative" data-id="element-3512">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3513">
                  <MailIcon size={14} className="text-gray-400" data-id="element-3514" />
                </div>
                <input type="email" className={`${inputClass} pl-9`} placeholder="customer@example.com" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} data-id="element-3515" />
              </div>
            </div>
            <div data-id="element-3516">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3517">
                Phone{' '}
                <span className="normal-case text-gray-400" data-id="element-3518">(optional)</span>
              </label>
              <div className="relative" data-id="element-3519">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3520">
                  <PhoneIcon size={14} className="text-gray-400" data-id="element-3521" />
                </div>
                <input type="tel" className={`${inputClass} pl-9`} placeholder="+66 81 234 5678" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} data-id="element-3522" />
              </div>
            </div>
          </div>
        </div>}

      {/* ─── SUBSCRIPTION DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-3523">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-3524">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-3525">
            <RepeatIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-3526" />
            Subscription details
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-3527">
          <div data-id="element-3528">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3529">
              Amount per billing period <span className="text-red-400" data-id="element-3530">*</span>
            </label>
            <div className="relative" data-id="element-3531">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3532">
                <span className="text-sm font-medium text-gray-500" data-id="element-3533">THB</span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={handleAmountChange} data-id="element-3534" />
            </div>
          </div>
          <div data-id="element-3535">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3536">
              Description <span className="text-red-400" data-id="element-3537">*</span>
            </label>
            <input type="text" className={inputClass} placeholder="e.g., Premium membership" value={description} onChange={e => setDescription(e.target.value)} data-id="element-3538" />
          </div>
          <div data-id="element-3539">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3540">
              Reference{' '}
              <span className="normal-case text-gray-400" data-id="element-3541">(optional)</span>
            </label>
            <input type="text" className={inputClass} placeholder="Internal reference" value={reference} onChange={handleReferenceChange} data-id="element-3542" />
          </div>
        </div>
      </div>

      {/* ─── BILLING SCHEDULE ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-3543">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-3544">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-3545">
            <ClockIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-3546" />
            Billing schedule
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-3547">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3548">
            {/* Searchable interval dropdown */}
            <div ref={intervalRef} className="relative" data-id="element-3549">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3550">
                Billing interval
              </label>
              <button type="button" className={`${inputClass} text-left flex items-center justify-between cursor-pointer`} onClick={() => {
              setIntervalOpen(!intervalOpen);
              setIntervalSearch('');
            }} data-id="element-3551">
                <span className={selectedIntervalLabel ? 'text-gray-900' : 'text-gray-400'} data-id="element-3552">
                  {selectedIntervalLabel || 'Select interval'}
                </span>
                <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${intervalOpen ? 'rotate-180' : ''}`} data-id="element-3553" />
              </button>
              {intervalOpen && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-3554">
                  <div className="p-2 border-b border-gray-100" data-id="element-3555">
                    <div className="relative" data-id="element-3556">
                      <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3557" />
                      <input type="text" className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] placeholder:text-gray-400" placeholder="Search..." value={intervalSearch} onChange={e => setIntervalSearch(e.target.value)} autoFocus data-id="element-3558" />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto" data-id="element-3559">
                    {filteredIntervals.length > 0 ? filteredIntervals.map(opt => <button key={opt.value} type="button" className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${billingInterval === opt.value ? 'bg-[#F7941D]/5 text-[#F7941D] font-medium' : 'text-gray-700'}`} onClick={() => {
                  setBillingInterval(opt.value);
                  setIntervalOpen(false);
                }} data-id="element-3560">
                          {opt.label}
                          {billingInterval === opt.value && <CheckIcon size={14} className="text-[#F7941D]" data-id="element-3561" />}
                        </button>) : <div className="px-3 py-4 text-sm text-gray-500 text-center" data-id="element-3562">
                        No results
                      </div>}
                  </div>
                </div>}
            </div>

            <div data-id="element-3563">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3564">
                Billing cycles{' '}
                <span className="normal-case text-gray-400" data-id="element-3565">(optional)</span>
              </label>
              <input type="number" min="1" className={inputClass} placeholder="Unlimited" value={billingCycles} onChange={e => setBillingCycles(e.target.value)} data-id="element-3566" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3567">
            {/* Date picker */}
            <div ref={datePickerRef} className="relative" data-id="element-3568">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3569">
                Start date{' '}
                <span className="normal-case text-gray-400" data-id="element-3570">(optional)</span>
              </label>
              <button type="button" className={`${inputClass} text-left flex items-center justify-between cursor-pointer`} onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-3571">
                <div className="flex items-center gap-2" data-id="element-3572">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3573" />
                  <span className={startDate ? 'text-gray-900' : 'text-gray-400'} data-id="element-3574">
                    {startDate ? formatDateFull(startDate) : 'Start immediately'}
                  </span>
                </div>
                <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} data-id="element-3575" />
              </button>

              {showDatePicker && <div className="absolute z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80 left-0" data-id="element-3576">
                  <div className="flex justify-between items-center mb-3" data-id="element-3577">
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} data-id="element-3578">
                      <ChevronLeftIcon size={14} data-id="element-3579" />
                    </button>
                    <div className="text-sm font-semibold text-gray-900" data-id="element-3580">
                      {calendarMonth.toLocaleString('default', {
                    month: 'long',
                    year: 'numeric'
                  })}
                    </div>
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} data-id="element-3581">
                      <ChevronRightIcon size={14} data-id="element-3582" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 mb-1" data-id="element-3583">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} data-id="element-3584">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center" data-id="element-3585">
                    {calendarWeeks.flatMap((week, wi) => week.map((date, di) => date ? <button key={`${wi}-${di}`} type="button" disabled={isDatePast(date)} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-colors ${isDateSelected(date) ? 'bg-[#F7941D] text-white' : isDateToday(date) ? 'border border-[#F7941D] text-[#F7941D]' : isDatePast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
                  setStartDate(date);
                  setShowDatePicker(false);
                }} data-id="element-3586">
                            {date.getDate()}
                          </button> : <div key={`${wi}-${di}`} className="w-8 h-8" data-id="element-3587" />))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2" data-id="element-3588">
                    <button type="button" className="flex-1 text-xs font-medium text-[#F7941D] hover:bg-[#F7941D]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                  setStartDate(null);
                  setShowDatePicker(false);
                }} data-id="element-3589">
                      Start immediately
                    </button>
                    <button type="button" className="flex-1 text-xs font-medium text-[#F7941D] hover:bg-[#F7941D]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                  setStartDate(new Date());
                  setShowDatePicker(false);
                }} data-id="element-3590">
                      Today
                    </button>
                  </div>
                </div>}
            </div>

            <div data-id="element-3591">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-3592">
                Trial period (days)
              </label>
              <input type="number" min="0" className={inputClass} placeholder="0" value={trialDays} onChange={e => setTrialDays(e.target.value)} data-id="element-3593" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── BILLING CYCLE PREVIEW ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-3594">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-3595">
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-3596">
            Billing cycle preview
          </h3>
          <span className="text-xs text-gray-400" data-id="element-3597">
            {billingCycles ? `${billingCycles} cycles` : 'Ongoing'} ·{' '}
            {selectedIntervalLabel}
          </span>
        </div>

        {billingPreview.length > 0 ? <div className="max-h-80 overflow-y-auto" data-id="element-3598">
            {billingPreview.map((cycle, idx) => <div key={idx} className={`flex items-center justify-between px-6 py-3 ${idx < billingPreview.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-3599">
                <div className="flex items-center gap-3" data-id="element-3600">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${cycle.isTrial ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-3601">
                    {cycle.isTrial ? 'T' : cycle.cycle}
                  </div>
                  <div data-id="element-3602">
                    <p className="text-sm text-gray-900" data-id="element-3603">
                      {formatDateShort(cycle.date)}
                    </p>
                    {cycle.isTrial && <p className="text-[11px] text-blue-500" data-id="element-3604">
                        Trial period · {trialDays} days free
                      </p>}
                  </div>
                </div>
                <span className={`text-sm font-medium ${cycle.isTrial ? 'text-blue-500' : 'text-gray-900'}`} data-id="element-3605">
                  {cycle.isTrial ? 'THB 0' : amount ? `THB ${amount}` : '—'}
                </span>
              </div>)}
            {!billingCycles && <div className="px-6 py-3 border-t border-gray-200 bg-gray-50/50 text-center" data-id="element-3606">
                <p className="text-xs text-gray-400" data-id="element-3607">Continues indefinitely…</p>
              </div>}
          </div> : <div className="px-6 py-8 text-center" data-id="element-3608">
            <p className="text-sm text-gray-500" data-id="element-3609">
              Enter an amount and billing interval to see the preview
            </p>
          </div>}
      </div>
    </div>;
}